import * as React from 'react';

import type {ClickAwayRefType} from '@spaced-out/ui-design-system';
import {FilterButtonOverlay} from '@spaced-out/ui-design-system/lib/components/FilterButtonOverlay';
import {SimpleDropdown} from '@spaced-out/ui-design-system/lib/components/Dropdown';
import {Button} from '@spaced-out/ui-design-system/lib/components/Button';
import {Input} from '@spaced-out/ui-design-system/lib/components/Input';
import {BodySmall} from '@spaced-out/ui-design-system/lib/components/Text';
import type {MenuOption} from '@spaced-out/ui-design-system/lib/components/Menu';

import useLabelTranslation from 'src/hooks/useLabelTranslation';
import type {SearchableColumnType} from 'src/types/byod';

import {
  EMPTY_TABLE_SEARCH,
  SEARCH_OPERATORS,
  buildTableSearchConfig,
  isTableSearchActive,
} from './table-search';
import type {SearchOperator, TableSearch} from './table-search';
import css from './byod-table-filter.css';


export {EMPTY_TABLE_SEARCH, buildTableSearchConfig, isTableSearchActive};
export type {SearchOperator, TableSearch};

/** One column the user can search on, with its type (drives the input mode). */
export type FilterableColumn = {
  key: string;
  label: string;
  type: SearchableColumnType;
};

/** Sentinel option key for "search across all columns" (no `search_column`). */
const ALL_COLUMNS_KEY = '__all_columns__';

export type ByodTableFilterProps = {
  /** Searchable columns offered in the column scope dropdown. */
  columns: FilterableColumn[];
  /** Currently applied search (drives the active state + seeds the panel). */
  value: TableSearch;
  /** Fired on Apply with the new search (an inactive search clears the filter). */
  onApply: (search: TableSearch) => void;
  buttonSize?: 'small' | 'medium';
};

/** Parse a numeric input; empty or non-finite ⇒ null (treated as "no value"). */
function parseNumber(raw: string): number | null {
  const trimmed = raw.trim();
  if (trimmed === '') {
    return null;
  }
  const n = Number(trimmed);
  return Number.isFinite(n) ? n : null;
}

/**
 * Inline table filter — a `FilterButtonOverlay` whose panel scopes a search to a
 * column (or all columns). Dimension columns get a free-text search; measure
 * columns get a numeric comparison (operator + value, or two values for
 * `BETWEEN`). Emits a `TableSearch` via `onApply`; the surface forwards it into
 * the query's `widget_config` (see `buildTableSearchConfig`) and re-fetches.
 *
 * Draft state lives in the panel, which the overlay mounts fresh on each open,
 * so it seeds from the applied `value` and a click-away discards edits.
 */
export function ByodTableFilter({
  columns,
  value,
  onApply,
  buttonSize = 'small',
}: ByodTableFilterProps) {
  const {t} = useLabelTranslation();
  const clickAwayRef = React.useRef<ClickAwayRefType>(null);

  const handleApply = React.useCallback(
    (search: TableSearch) => {
      onApply(search);
      clickAwayRef.current?.forceClose();
    },
    [onApply],
  );

  return (
    <FilterButtonOverlay
      isFluid={false}
      type={isTableSearchActive(value) ? 'secondary' : 'tertiary'}
      buttonSize={buttonSize}
      iconLeftName="filter"
      clickAwayRef={clickAwayRef}
      anchorPosition="bottom-end"
      buttonLabel={t('Filter', 'Filter')}
      testId="byod-table-filter"
      classNames={{overlayContainer: css.overlayContainer}}
    >
      <ByodTableFilterPanel
        columns={columns}
        value={value}
        onApply={handleApply}
      />
    </FilterButtonOverlay>
  );
}

function ByodTableFilterPanel({
  columns,
  value,
  onApply,
}: {
  columns: FilterableColumn[];
  value: TableSearch;
  onApply: (search: TableSearch) => void;
}) {
  const {t} = useLabelTranslation();

  const [column, setColumn] = React.useState<string>(
    value.search_column ?? ALL_COLUMNS_KEY,
  );
  const [text, setText] = React.useState<string>(value.search_text ?? '');
  const [operator, setOperator] = React.useState<SearchOperator>(
    value.search_operator ?? '=',
  );
  const [value1, setValue1] = React.useState<string>(
    value.search_value != null ? String(value.search_value) : '',
  );
  const [value2, setValue2] = React.useState<string>(
    value.search_value_2 != null ? String(value.search_value_2) : '',
  );

  const selectedColumn = columns.find((c) => c.key === column);
  // "All columns" (no selected column) is always a text search.
  const isMeasure = selectedColumn?.type === 'measure';

  const columnOptions = React.useMemo<MenuOption[]>(
    () => [
      {key: ALL_COLUMNS_KEY, label: t('All_columns', 'All columns')},
      ...columns.map((c) => ({key: c.key, label: c.label})),
    ],
    [columns, t],
  );

  const operatorOptions = React.useMemo<MenuOption[]>(
    () =>
      SEARCH_OPERATORS.map((op) => ({
        key: op,
        label: op === 'BETWEEN' ? t('Between', 'Between') : op,
      })),
    [t],
  );

  const buildDraft = React.useCallback((): TableSearch => {
    if (isMeasure) {
      const draft: TableSearch = {
        search_column: column,
        search_operator: operator,
        search_value: parseNumber(value1),
      };
      if (operator === 'BETWEEN') {
        draft.search_value_2 = parseNumber(value2);
      }
      return draft;
    }
    return {
      search_column: column === ALL_COLUMNS_KEY ? undefined : column,
      search_text: text.trim(),
    };
  }, [isMeasure, column, operator, value1, value2, text]);

  return (
    <div className={css.panel}>
      <div className={css.fields}>
        <div className={css.field}>
          <BodySmall color="secondary">{t('Column', 'Column')}</BodySmall>
          <SimpleDropdown
            size="small"
            options={columnOptions}
            selectedKeys={[column]}
            onChange={({key}) => setColumn(key)}
            allowSearch={columnOptions.length > 6}
            classNames={{wrapper: css.fieldControl}}
          />
        </div>

        {isMeasure ? (
          <>
            <div className={css.field}>
              <BodySmall color="secondary">
                {t('Condition', 'Condition')}
              </BodySmall>
              <SimpleDropdown
                size="small"
                options={operatorOptions}
                selectedKeys={[operator]}
                onChange={({key}) => setOperator(key as SearchOperator)}
                classNames={{wrapper: css.fieldControl}}
              />
            </div>
            <div className={css.field}>
              <BodySmall color="secondary">{t('Value', 'Value')}</BodySmall>
              {operator === 'BETWEEN' ? (
                <div className={css.rangeRow}>
                  <Input
                    size="small"
                    type="number"
                    placeholder={t('From', 'From')}
                    value={value1}
                    onChange={(evt) => setValue1(evt.target.value)}
                    classNames={{wrapper: css.fieldControl}}
                  />
                  <Input
                    size="small"
                    type="number"
                    placeholder={t('To', 'To')}
                    value={value2}
                    onChange={(evt) => setValue2(evt.target.value)}
                    classNames={{wrapper: css.fieldControl}}
                  />
                </div>
              ) : (
                <Input
                  size="small"
                  type="number"
                  placeholder={t('Enter_value', 'Enter value')}
                  value={value1}
                  onChange={(evt, isEnter) => {
                    setValue1(evt.target.value);
                    if (isEnter) {
                      onApply(buildDraft());
                    }
                  }}
                  classNames={{wrapper: css.fieldControl}}
                />
              )}
            </div>
          </>
        ) : (
          <div className={css.field}>
            <BodySmall color="secondary">{t('Search', 'Search')}</BodySmall>
            <Input
              size="small"
              type="search"
              iconLeftName="magnifying-glass"
              placeholder={t('Enter_search_text', 'Enter search text')}
              value={text}
              onChange={(evt, isEnter) => {
                setText(evt.target.value);
                if (isEnter) {
                  onApply(buildDraft());
                }
              }}
              classNames={{wrapper: css.fieldControl}}
            />
          </div>
        )}
      </div>

      <div className={css.footer}>
        <Button
          type="ghost"
          size="small"
          onClick={() => {
            // Reset the draft only; the panel stays open and nothing is
            // committed until Apply.
            setColumn(ALL_COLUMNS_KEY);
            setText('');
            setOperator('=');
            setValue1('');
            setValue2('');
          }}
        >
          {t('Clear_all', 'Clear all')}
        </Button>
        <Button
          type="primary"
          size="small"
          onClick={() => onApply(buildDraft())}
        >
          {t('Apply', 'Apply')}
        </Button>
      </div>
    </div>
  );
}
